import { AspectRatio } from '@src/types.ts/video';
import { ImageElement } from '../../models';

type LogoDimensions = {
  x: number;
  y: number;
  width: number;
};

/**
 * Handles logo positioning and sizing across different aspect ratios
 */
export class LogoPositioner {
  private prevAspectRatio: AspectRatio;
  private newAspectRatio: AspectRatio;
  private element: ImageElement;
  private prevDimensions: LogoDimensions;
  private newDimensions: LogoDimensions;

  /**
   * Create a logo positioner
   * @param prevAspectRatio Previous aspect ratio
   * @param newAspectRatio New aspect ratio
   * @param element The logo element to position
   */
  constructor(
    prevAspectRatio: AspectRatio,
    newAspectRatio: AspectRatio,
    element: ImageElement,
  ) {
    this.prevAspectRatio = prevAspectRatio;
    this.newAspectRatio = newAspectRatio;
    this.element = element;
    this.prevDimensions =
      LogoPositioner.getDimensionsForAspectRatio(prevAspectRatio);
    this.newDimensions =
      LogoPositioner.getDimensionsForAspectRatio(newAspectRatio);
  }

  /**
   * Get adjusted dimensions for the logo element
   * @param sourceAspectRatio The source aspect ratio of the logo image
   */
  getAdjustedDimensions(
    sourceAspectRatio: number,
  ): Record<'x' | 'y' | 'width' | 'height', string> {
    // Calculate width based on new aspect ratio's logo dimensions
    const width = this.calculateAdjustedWidth();

    // Calculate position and dimensions
    return {
      width: `${width}%`,
      height: this.calculateHeight(width, sourceAspectRatio),
      x: this.calculateX(width),
      y: this.calculateY(),
    };
  }

  /**
   * Calculate the adjusted width based on new aspect ratio's logo dimensions
   */
  private calculateAdjustedWidth(): number {
    return (
      (this.newDimensions.width / this.prevDimensions.width) *
      parseFloat(this.element.width!)
    );
  }

  /**
   * Calculate height based on width and source aspect ratio
   */
  private calculateHeight(width: number, sourceAspectRatio: number): string {
    const targetAspectRatio = LogoPositioner.getCanvasAspectRatio(
      this.newAspectRatio,
    );
    return `${(width / sourceAspectRatio) * targetAspectRatio}%`;
  }

  /**
   * Calculate X position
   */
  private calculateX(newWidth: number): string {
    const currentX = parseFloat(this.element.x!);
    const currentWidth = parseFloat(this.element.width!);
    const leftEdgePos = currentX - currentWidth;

    // If logo was at the default position, keep it at the new default
    if (currentX === this.prevDimensions.x) {
      return `${this.newDimensions.x}%`;
    }

    // For logos positioned on the right side of the canvas
    if (currentX >= 50) {
      return `${(this.newDimensions.x / this.prevDimensions.x) * currentX}%`;
    }

    // For logos positioned on the left side, ensure they don't go off-screen
    return `${Math.max(
      (this.newDimensions.x / this.prevDimensions.x) * currentX,
      newWidth + leftEdgePos * (this.newDimensions.x / this.prevDimensions.x),
    )}%`;
  }

  /**
   * Calculate Y position
   */
  private calculateY(): string {
    const currentY = parseFloat(this.element.y!);
    const scaleFactor =
      (100 - this.newDimensions.y) / (100 - this.prevDimensions.y);
    return `${scaleFactor * currentY}%`;
  }

  // Keep static methods for utility functions
  static getDimensionsForAspectRatio(aspectRatio: AspectRatio): LogoDimensions {
    switch (aspectRatio) {
      case AspectRatio.AR_16_9:
        return {
          x: 94,
          y: 4,
          width: 12,
        };
      case AspectRatio.AR_9_16:
        return {
          x: 92,
          y: 5,
          width: 25,
        };
      case AspectRatio.AR_1_1:
      default:
        return {
          x: 95,
          y: 5,
          width: 19,
        };
    }
  }

  static getCanvasAspectRatio(aspectRatio: AspectRatio): number {
    switch (aspectRatio) {
      case AspectRatio.AR_16_9:
        return 16 / 9;
      case AspectRatio.AR_9_16:
        return 9 / 16;
      case AspectRatio.AR_1_1:
      default:
        return 1;
    }
  }
}
